import { z } from 'zod';
import { datenregelErgebnisSchema } from './datenregeln.js';
import { isoDate, uuid } from './posting.js';
/**
 * **Der Buchungssatz am Beispiel** — Auftrag `DR-buchungsvorschau`
 * (2026-10-15), Task 18 des Plans
 * `docs/superpowers/plans/2026-09-01-datenregeln-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-01-datenregeln-design.md` Abschnitt 17.4
 * Punkt 5 (`D-31`, `D-38`).
 *
 * `POST /api/v1/datenregeln/buchungsvorschau` zeigt für EINE gewählte
 * Bewegung, welchen Buchungssatz das Ergebnis einer Regel ergäbe — Soll,
 * Haben, Steuerzeile. Der Satz wird **nicht nachgerechnet**: Der Dienst ruft
 * dieselbe reine Funktion wie der Buchungspfad
 * (`baueKontierungsbuchung()`, `apps/api/src/kontierung/buchung.ts`) mit
 * denselben aufgelösten Konten. Diese Datei beschreibt nur den Umschlag.
 *
 * **Der Rumpf trägt das ERGEBNIS einer Regel und keine Kriterien.** Welche
 * Bewegung gemeint ist, sagt der Anlegende mit einem Klick auf einen Treffer
 * der Trefferliste; ob die Regel diese Bewegung überhaupt träfe, ist die
 * Frage der Vorschau daneben und nicht dieser Strecke. Das Ergebnis ist
 * dasselbe Schema wie das der Fassung ({@link datenregelErgebnisSchema}) —
 * eine zweite Formulierung desselben Gegenstands wäre die Stelle, an der die
 * Vorschau eines Tages etwas anderes annimmt, als die Regel speichert.
 */
export const datenregelBuchungsvorschauSchema = z
    .object({
    bankTransactionId: uuid,
    ergebnis: datenregelErgebnisSchema,
})
    .strict();
/** Die Seiten einer Buchungszeile. */
export const DATENREGEL_BUCHUNGSSEITEN = ['soll', 'haben'];
/**
 * Eine Zeile des Buchungssatzes.
 *
 * **Der Betrag ist eine Zeichenkette ohne Vorzeichen** (Hausregel 1): Die
 * Seite trägt die Richtung, wie in jeder Journalzeile. Er kommt aus
 * `baueKontierungsbuchung()` und geht nie durch `number`.
 */
export const datenregelBuchungszeileSchema = z
    .object({
    konto: z.string(),
    seite: z.enum(DATENREGEL_BUCHUNGSSEITEN),
    betrag: z.string(),
    /** Der Steuerschlüssel der Zeile, wo die Funktion einen mitgibt. */
    steuerschluessel: z.string().nullable(),
    /** Die Zeilenbeschreibung der Funktion ("Geldkonto der Bewegung", "Vorsteuer", …), sonst `null`. */
    beschreibung: z.string().nullable(),
})
    .strict();
/**
 * Die benannten Befunde, mit denen die Strecke antwortet, **statt zu
 * werfen** (Haltung von `E-597`: Ein Befund über Kontenrahmen oder Stammdaten
 * ist keine kaputte Anfrage).
 *
 * - `konto_ungueltig` — das Sachkonto hat zum Buchungsdatum der Bewegung
 *   keine gültige Fassung (die Funktion nennt es `konto_nicht_benannt`).
 * - `schluessel_ungueltig` — der Steuerschlüssel existiert am Buchungsdatum
 *   nicht oder ist die Marke `ABSTIMMEN`.
 * - `doppelzaehlung` — der Buchungspfad würde diese Kombination aus
 *   Bewegung und Erfolgskonto abweisen (`E-490`, `E-491`): Die Auszahlung
 *   eines Zahldienstes ist eine Umbuchung, die PayPal-Lastschrift
 *   finanziert nur das Zahldienstkonto.
 */
export const DATENREGEL_BUCHUNGSBEFUNDE = [
    'konto_ungueltig',
    'schluessel_ungueltig',
    'doppelzaehlung',
];
/**
 * Die Antwort: entweder der Buchungssatz oder ein benannter Befund — nie ein
 * Fehlerstatus für einen Befund.
 *
 * **Eigene Felder, keine Verschmelzung mit der Antwort der Trefferprobe**
 * (`datenregelVorschauAntwortSchema`): Die zwei Strecken sind zwei
 * Endpunkte; was die eine wächst, fasst diese nicht an.
 */
export const datenregelBuchungsvorschauAntwortSchema = z.discriminatedUnion('art', [
    z
        .object({
        art: z.literal('buchungssatz'),
        bankTransactionId: uuid,
        buchungsdatum: isoDate,
        beschreibung: z.string(),
        zeilen: z.array(datenregelBuchungszeileSchema).min(1),
    })
        .strict(),
    z
        .object({
        art: z.literal('befund'),
        befund: z.enum(DATENREGEL_BUCHUNGSBEFUNDE),
        bankTransactionId: uuid,
        buchungsdatum: isoDate,
        /** Der Satz für die Maske, in der Sprache des Anlegenden. */
        meldung: z.string(),
    })
        .strict(),
]);
//# sourceMappingURL=datenregel-buchungsvorschau.js.map